import { format } from "date-fns";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Text } from "@performyard/py-components";
import { Avatar } from "@performyard/py-components";
import {
	MetaDataReviewInsightResponseStreamJson,
	ReviewInsightResponseStreamJson,
	UserErrorResponse,
	UserErrorResponseJson,
} from "@performyard/service-contracts";

import { useAiQuery } from "../../../api/hooks";

import { AISidePanel } from "../SidePanel";

export type SummarizeReviewModuleProps = {
	reviewId: string;
	isOpen: boolean;
	onOpenChange: (isOpen: boolean) => void;
};

export type StreamedData = {
	data: ReviewInsightResponseStreamJson | UserErrorResponse;
	inProgress: boolean;
} | null;

export const SummarizeReviewModule = ({ reviewId, isOpen, onOpenChange }: SummarizeReviewModuleProps) => {
	const [recreateCount, setRecreateCount] = useState(0);
	const [retryIsProcessing, setRetryIsProcessing] = useState(false);

	const { data, isLoading, isFetching, isRefetching } = useAiQuery<
		MetaDataReviewInsightResponseStreamJson | UserErrorResponse
	>({
		type: "review-insight-haiku",
		reviewId,
		findAdditionalContext: false,
		retry: recreateCount > 0 ? "true" : "false",
		responseStyle: "structured",
		recreateCount,
	});

	const inProgress = useMemo(() => {
		if (isLoading || isRefetching || retryIsProcessing) return true;
		if (data && typeof data === "object" && "status" in data) {
			return data.status === "in_progress";
		}
		return false;
	}, [data, isLoading, isRefetching, retryIsProcessing]);

	const isError = (
		response: UserErrorResponseJson | MetaDataReviewInsightResponseStreamJson | undefined
	): response is UserErrorResponseJson => {
		const fallbackErrorMessage =
			"Insights are not available for this review. At least one form must be completed by an author before insights can be generated.";
		if (!response || inProgress || retryIsProcessing) return false;
		// Successful (in-progress envelope or completed insight) shape: has `status`.
		if (response && typeof response === "object" && "status" in response) {
			return false;
		}
		// UserErrorResponse with `data.message` (legacy nested shape from postQueryAi catch).
		if (
			response &&
			typeof response === "object" &&
			"data" in response &&
			typeof (response as { data?: unknown }).data === "object" &&
			response.data !== null &&
			"message" in (response.data as Record<string, unknown>)
		) {
			const nestedMessage = (response.data as { message?: unknown }).message;
			if (typeof nestedMessage === "string" && nestedMessage.length > 0) {
				response.message = nestedMessage;
			} else {
				response.message = fallbackErrorMessage;
			}
			return true;
		}
		// Standard UserErrorResponse: top-level `message`.
		if (response && typeof response === "object" && "message" in response) {
			if (typeof response.message !== "string" || response.message.length === 0) {
				response.message = fallbackErrorMessage;
			}
			// otherwise leave the backend-provided message as-is
			return true;
		}
		return false;
	};

	const handleRetry = useCallback(() => {
		setRetryIsProcessing(true);
		setRecreateCount((prev) => prev + 1);
	}, []);

	useEffect(() => {
		if (!isFetching && retryIsProcessing) {
			setRetryIsProcessing(false);
		}
	}, [isFetching, retryIsProcessing]);

	const renderHeader = (data: ReviewInsightResponseStreamJson) => (
		<div className="border-radius-1 flex w-full flex-col items-center justify-center gap-4 self-stretch border-b border-solid border-neutral-200 pb-4">
			<div className="flex items-start gap-5 self-stretch rounded bg-neutral-100 p-2">
				<div className="flex flex-col items-start justify-start">
					<Avatar size={32} id={data?.subject?._id || ""} name={data?.subject?.name || "User"} />
				</div>
				<div className="flex flex-1 shrink-0 basis-0 flex-col items-start gap-2">
					<Text size="paragraph-1" weight="semi-bold" color="neutral-9">
						Review of {data?.subject?.name || ""}
					</Text>

					<div className="flex flex-col items-start gap-1">
						<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
							Review Started:{" "}
							<Text size="paragraph-3" weight="regular" color="neutral-9" overrideElement="span">
								{data.startDate && Date.parse(data.startDate)
									? format(new Date(data.startDate), "MMMM d, yyyy 'at' h:mm a")
									: (data.startDate ?? "Not started")}
							</Text>
						</Text>
						<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
							Review Template:{" "}
							<Text size="paragraph-3" weight="regular" color="neutral-9" overrideElement="span">
								{data?.title || ""}
							</Text>
						</Text>

						{data?.completedDate && (
							<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
								Completed:{" "}
								<Text size="paragraph-3" weight="regular" color="neutral-9" overrideElement="span">
									{/* is valid date if so then  format it */}
									{data.completedDate && Date.parse(data.completedDate)
										? format(new Date(data.completedDate), "MMMM d, yyyy 'at' h:mm a")
										: (data.completedDate ?? "Not completed")}
								</Text>
							</Text>
						)}
					</div>
				</div>
			</div>
		</div>
	);

	const renderInfoSection = (data: ReviewInsightResponseStreamJson) => (
		<div className="flex flex-col gap-1">
			<Text size="caption" weight="regular" color="neutral-9">
				Summarization Populated:{" "}
				{data.createdAt
					? format(new Date(data.createdAt), "MMMM d, yyyy 'at' h:mm a")
					: format(new Date(), "MMMM d, yyyy 'at' h:mm a")}
			</Text>
			{!data.completed && (
				<Text size="caption" weight="regular" color="neutral-9">
					**Generated From an Incomplete Review
				</Text>
			)}
		</div>
	);

	return (
		<AISidePanel<MetaDataReviewInsightResponseStreamJson>
			type="review"
			isOpen={isOpen}
			onOpenChange={onOpenChange}
			data={data && !isError(data) ? data : null}
			isLoading={inProgress}
			renderHeader={renderHeader}
			renderInfoSection={renderInfoSection}
			onRetry={handleRetry}
			error={isError(data) ? data : null}
			logoLoaderProps={{
				textArray: [
					"Compiling Forms…",
					"Retrieving Review Participants…",
					"Organizing Review Content…",
					"Processing Responses…",
					"Generating Summarization…",
					"Structuring Summary Format…",
					"Finalizing Results..",
					"Finishing Up…",
				],
				mainText: "Summarizing Review",
			}}
		/>
	);
};
